// Is Red Flag Rules switched on on this server (RED_FLAG_RULES_ENABLED)?
//
// Until it is, the web app must look exactly as it did before the feature existed, so
// every control it adds outside its own page (the Access Management capability row,
// the Dashboard Red Flag "Level" filter) renders only when the server says so. Anything
// short of an explicit `enabled: true` — a 404 from an older API, a network error, a
// sign-in problem — reads as OFF.
//
// Asked once per page load and shared: a definite answer (a 2xx, or a 404 = the route
// does not exist) is cached in module scope; a transient failure answers OFF and lets
// the next mount ask again.
import { useEffect, useState } from 'react';
import { redFlagRulesAPI } from '../../services/api';

let known: boolean | null = null;
let inFlight: Promise<boolean> | null = null;

export const loadRedFlagRulesEnabled = (): Promise<boolean> => {
  if (known !== null) return Promise.resolve(known);
  if (inFlight) return inFlight;
  let request: Promise<boolean>;
  try {
    request = redFlagRulesAPI.status().then(
      (res) => {
        known = res?.data?.data?.enabled === true;
        return known;
      },
      (e: unknown) => {
        if ((e as { response?: { status?: number } })?.response?.status === 404) known = false;
        return false;
      },
    );
  } catch {
    // The API module could not even build the request: treat as off, ask again later.
    return Promise.resolve(false);
  }
  inFlight = request.finally(() => { inFlight = null; });
  return inFlight;
};

/**
 * true only once the server has said the feature is on. `active` = false skips the
 * request (e.g. a tab that is not showing) and keeps whatever is already known.
 */
export const useRedFlagRulesEnabled = (active = true): boolean => {
  const [enabled, setEnabled] = useState<boolean>(known === true);
  useEffect(() => {
    if (!active) return undefined;
    let alive = true;
    void loadRedFlagRulesEnabled().then((on) => { if (alive) setEnabled(on); });
    return () => { alive = false; };
  }, [active]);
  return enabled;
};

/** Tests only: forget the cached answer. */
export const resetRedFlagRulesStatusForTests = (): void => {
  known = null;
  inFlight = null;
};
